import { Component, h, State, Prop, Element } from '@stencil/core';
import { IFooter } from '../utils/interfaces/footer.interface';
import { fetchNavigationData, fetchMockNavigationData } from '../utils/services/navigation.service';
import { htmlSanitizer } from '../../../utils/html-sanitizer';

@Component({
  tag: 'visa-footer',
  styleUrl: 'visa-footer.css',
  shadow: false,
})
export class VisaFooter {
  @Element() el!: HTMLElement;

  /**
   * The footer data
   */
  @Prop() content?: any;

  /**
   * Use mock data for development
   */
  @Prop() useMockData: boolean = false;

  /**
   * Custom URL to fetch navigation data from
   */
  @Prop() dataUrl?: string;

  /**
   * Internal state for footer data
   */
  @State() footerData: IFooter;

  /**
   * Whether to use thin footer layout
   */
  @State() isThinFooter: boolean = false;

  /**
   * Country dropdown state
   */
  @State() isCountryDropdownOpen: boolean = false;

  /**
   * Social dropdown state
   */
  @State() socialDropdownOpen: { [key: string]: boolean } = {};

  /**
   * Current year for copyright
   */
  @State() currentYear: string = new Date().getFullYear().toString();

  @State() selectedCountry: string = 'United States';

  @State() selectedLanguage: string = 'English';

  @State() showCountryPanel: boolean = false;

  private panelRightEl!: HTMLElement; // ref to scroll container

  /**
   * Active country option index for keyboard navigation
   */
  @State() activeCountryIndex: number = -1;

  /**
   * Reference to the language selector element for focus management
   */
  private languageSelectorRef?: HTMLElement;

  /**
   * Component lifecycle method
   * Called once when the component is first connected to the DOM
   */
  async componentWillLoad() {
    // If data is provided via prop, use it
    if (this.content) {
      try {
        // Check if content is already a JSON object or a string that needs parsing
        let parsedFooter;
        if (typeof this.content === 'string') {
          parsedFooter = JSON.parse(this.content);
        } else {
          parsedFooter = this.content;
        }
        this.footerData = parsedFooter.footer || parsedFooter || {};
        this.selectedCountry =
          this.findDefaultCountryByHostname(this.footerData.countries.options) || 'United States';
        this.selectedLanguage = this.footerData.countries.label || 'English';
      } catch (error) {
        console.error('Failed to parse footer content:', error);
      }
    } else {
      // Otherwise fetch data
      try {
        if (this.useMockData) {
          const data = await fetchMockNavigationData(this.dataUrl);
          this.footerData = data.footer;
          this.selectedCountry =
            this.findDefaultCountryByHostname(this.footerData.countries.options) || 'United States';
          this.selectedLanguage = this.footerData.countries.label || 'English';
        } else {
          const data = await fetchNavigationData(this.dataUrl);
          this.footerData = data.footer;
          this.selectedCountry =
            this.findDefaultCountryByHostname(this.footerData.countries.options) || 'United States';
          this.selectedLanguage = this.footerData.countries.label || 'English';
        }
      } catch (error) {
        console.error('Failed to load footer data:', error);
      }
    }

    // Check if we should use thin footer layout
    this.checkThinFooter();
  }

  /**
   * Check if we should use thin footer layout
   */
  private checkThinFooter() {
    if (this.footerData?.nav && this.footerData.nav.length === 1) {
      this.isThinFooter = true;
    }
  }

  /**
   * Toggle country dropdown
   */
  private toggleCountryDropdown = (e?: Event) => {
    if (e) e.stopPropagation();
    this.showCountryPanel = !this.showCountryPanel;
  };

  /**
   * Handle keyboard events for language selector
   */
  private handleLanguageSelectorKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.showCountryPanel = !this.showCountryPanel;
      if (this.showCountryPanel) {
        // Reset active index when opening
        this.activeCountryIndex = -1;
        // Focus first country option after a brief delay
        setTimeout(() => {
          this.focusCountryOption(0);
        }, 100);
      }
    } else if (e.key === 'Escape' && this.showCountryPanel) {
      e.preventDefault();
      this.closeCountryPanelAndRestoreFocus();
    } else if (e.key === 'ArrowDown' && this.showCountryPanel) {
      e.preventDefault();
      this.navigateCountryOptions('down');
    } else if (e.key === 'ArrowUp' && this.showCountryPanel) {
      e.preventDefault();
      this.navigateCountryOptions('up');
    }
  };

  /**
   * Handle keyboard events for country selection
   */
  private handleCountryKeyDown = (key: string, value: string, e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleCountrySelection(key, value, e as any);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.closeCountryPanelAndRestoreFocus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.navigateCountryOptions('down');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.navigateCountryOptions('up');
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.focusCountryOption(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      const countryCount = this.getCountryOptionsCount();
      this.focusCountryOption(countryCount - 1);
    }
  };

  /**
   * Navigate through country options with arrow keys
   */
  private navigateCountryOptions = (direction: 'up' | 'down') => {
    const countryCount = this.getCountryOptionsCount();
    if (countryCount === 0) return;

    let newIndex = this.activeCountryIndex;

    if (direction === 'down') {
      newIndex = this.activeCountryIndex < countryCount - 1 ? this.activeCountryIndex + 1 : 0;
    } else {
      newIndex = this.activeCountryIndex > 0 ? this.activeCountryIndex - 1 : countryCount - 1;
    }

    this.focusCountryOption(newIndex);
  };

  /**
   * Focus a specific country option by index
   */
  private focusCountryOption = (index: number) => {
    this.activeCountryIndex = index;
    const countryOptions = this.el.querySelectorAll('.visa-footer-card-item[role="option"]');
    if (countryOptions[index]) {
      (countryOptions[index] as HTMLElement).focus();
    }
  };

  /**
   * Get the count of available country options
   */
  private getCountryOptionsCount = (): number => {
    if (this.footerData?.countries?.options) {
      return this.footerData.countries.options.length;
    }
    // Fallback count for default countries
    return 4;
  };

  /**
   * Close country panel and restore focus to language selector
   */
  private closeCountryPanelAndRestoreFocus = () => {
    this.showCountryPanel = false;
    this.activeCountryIndex = -1;
    // Restore focus to language selector
    if (this.languageSelectorRef) {
      this.languageSelectorRef.focus();
    }
  };

  /**
   * Handle document click to close dropdown
   */
  private handleDocumentClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const selector = target.closest('.language-selector');
    const socialSelector = target.closest('.vs-footer-social-dropdown');

    if (!selector) {
      this.showCountryPanel = false;
    }

    if (!socialSelector) {
      this.socialDropdownOpen = {};
    }
  };

  /**
   * Toggle social dropdown
   */
  private toggleSocialDropdown = (name: string, e?: Event) => {
    if (e) e.stopPropagation();
    this.socialDropdownOpen = {
      ...this.socialDropdownOpen,
      [name]: !this.socialDropdownOpen[name],
    };
  };

  /**
   * Component lifecycle method
   * Called after the component has loaded
   */
  componentDidLoad() {
    document.addEventListener('click', this.handleDocumentClick);
    if (this.panelRightEl) {
      this.panelRightEl.addEventListener('scroll', () => this.updateEndStyles(true));
      this.updateEndStyles(false); // run on load
    }
  }

  /**
   * Component lifecycle method
   * Called when the component is removed from the DOM
   */
  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  private updateEndStyles(scrolling = false) {
    const container = this.panelRightEl;
    const items = container.querySelectorAll<HTMLLIElement>('.visa-footer-card-item');

    items.forEach(item => item.classList.remove('dim-1', 'dim-2'));

    if (items.length < 2) return;
    const containerRect = container.getBoundingClientRect();
    let totalHeight = 0;
    let cutoffIndex = -1;

    const visibleThreshold = scrolling ? 600 : 520;
    const nonVisibleThreshold = 560;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const itemRect = item.getBoundingClientRect();

      const isVisible =
        itemRect.bottom >= containerRect.top && itemRect.top <= containerRect.bottom;

      if (isVisible) {
        totalHeight += item.clientHeight;

        if (totalHeight >= visibleThreshold) {
          cutoffIndex = i;
          break;
        }
      } else {
        if (totalHeight >= nonVisibleThreshold) {
          cutoffIndex = 12;
          break;
        }
      }
    }

    if (cutoffIndex !== -1 && cutoffIndex < items.length - 1) {
      items[cutoffIndex].classList.add('dim-1');
      items[cutoffIndex + 1].classList.add('dim-2');
    }
  }

  /**
   * Render the navigation sections
   */
  private renderNavigationSections() {
    // Use the nav data from footerData to create the sections
    if (!this.footerData?.nav || this.footerData.nav.length === 0) {
      return null;
    }

    return (
      <nav class="navigation-sections visa-footer" role="navigation" aria-label="Footer Resource">
        {this.footerData.nav.map((cluster, index) => (
          <section
            class="nav-section visa-footer"
            key={`section-${index}`}
            aria-labelledby={`nav-heading-${index}`}
          >
            <h2 class="subhead visa-footer" id={`nav-heading-${index}`}>
              {cluster.label}
            </h2>
            <ul class="nav-list visa-footer">
              {cluster.nav.map((link, linkIndex) => (
                <li class="nav-item visa-footer" key={`link-${linkIndex}`}>
                  <a
                    class="visa-footer"
                    href={link.link}
                    target={link.target === 'new' ? '_blank' : '_self'}
                    rel={link.linkType === 'external' ? 'noopener noreferrer' : null}
                    aria-label={link.ally_label || link.text || `Navigate to ${link.link}`}
                    innerHTML={htmlSanitizer(link.text)}
                  />

                  {link.target === 'new' && (
                    <span class="arrow-icon visa-footer" aria-hidden="true">
                      <svg
                        class="rotate-up-right"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  )}
                  {/*{link.target === 'same' && (
                    <span class="arrow-icon visa-footer" aria-hidden="true">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  )} */}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>
    );
  }

  /**
   * Get the current hostname for default country selection
   */
  private getCurrentHostname() {
    return window.location.hostname;
  }

  /**
   * Find the default country based on hostname
   */
  private findDefaultCountryByHostname(options: any[]) {
    const hostname = this.getCurrentHostname();

    // Find the option that matches the current hostname
    const matchedOption = options.find(option => {
      if (option.value && typeof option.value === 'string') {
        try {
          // Use a simple regex to extract hostname from URL
          const hostnameMatch = option.value.match(/\/\/([^/]+)/);
          const urlHostname = hostnameMatch ? hostnameMatch[1] : '';
          return urlHostname === hostname;
        } catch {
          // Ignore errors and return false
          return false;
        }
      }
      return false;
    });

    return matchedOption ? matchedOption.key : this.footerData.countries.default || '';
  }

  private handleCountrySelection(key: string, value: string, e: Event) {
    e.preventDefault();
    this.selectedCountry = key;
    this.selectedLanguage = key;
    const link = document.createElement('a');
    link.href = value;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
    this.closeCountryPanel();
  }

  private closeCountryPanel() {
    this.showCountryPanel = !this.showCountryPanel;
  }

  /**
   * Render the language selector
   */
  private renderLanguageSelector() {
    // Use the countries data from footerData
    if (!this.footerData?.countries) {
      // Fallback to default if no countries data is provided
      return (
        <div
          class="language-selector visa-footer"
          onClick={this.toggleCountryDropdown}
          onKeyDown={this.handleLanguageSelectorKeyDown}
          role="button"
          tabindex="0"
          aria-label="Select country and language. Currently selected: US - English"
          aria-expanded={this.showCountryPanel ? 'true' : 'false'}
          aria-haspopup="dialog"
          ref={el => (this.languageSelectorRef = el)}
        >
          <span class="language-icon visa-footer">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M2 12H22"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <span class="country visa-footer">US</span>
          <span class="separator visa-footer">|</span>
          <span class="language visa-footer">English</span>
          <span class={`chevron-down visa-footer ${this.showCountryPanel ? 'open' : ''}`}>
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L5 5L9 1"
                stroke="#D8D8D8"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>

          {this.isCountryDropdownOpen && (
            <div class="country-dropdown visa-footer">
              <ul class="visa-footer">
                <li class="analytics-footer-button active visa-footer">
                  <span class="country-name visa-footer">United States</span>
                </li>
                <li class="analytics-footer-button visa-footer">
                  <span class="country-name visa-footer">Canada</span>
                </li>
                <li class="analytics-footer-button visa-footer">
                  <span class="country-name visa-footer">Mexico</span>
                </li>
                <li class="analytics-footer-button visa-footer">
                  <span class="country-name visa-footer">United Kingdom</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        class="language-selector visa-footer"
        onClick={this.toggleCountryDropdown}
        onKeyDown={this.handleLanguageSelectorKeyDown}
        role="button"
        tabindex="0"
        aria-label={`Select country and language. Currently selected: ${this.selectedCountry || 'US'} - ${this.selectedLanguage || 'English'}`}
        aria-expanded={this.showCountryPanel ? 'true' : 'false'}
        aria-haspopup="dialog"
        ref={el => (this.languageSelectorRef = el)}
      >
        <span class="language-icon visa-footer">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M2 12H22"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>

        <span class="country visa-footer">{this.selectedCountry || 'US'}</span>
        <span class="separator visa-footer">|</span>
        <span class="language visa-footer">{this.selectedLanguage || 'English'}</span>

        <span class={`chevron-down visa-footer ${this.showCountryPanel ? 'open' : ''}`}>
          <svg
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L5 5L9 1"
              stroke="#D8D8D8"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </div>
    );
  }

  /**
   * Render the social icons
   */
  private renderSocialIcons() {
    // Use the socialLinks data from footerData
    if (!this.footerData?.socialLinks || this.footerData.socialLinks.length === 0) {
      // Fallback to default social icons if no data is provided
      return (
        <ul class="social-icons" role="list" aria-label="Social media links">
          <li class="social-icon-item">
            <a href="#" class="visa-footer social-icon" aria-label="Facebook">
              <div class="icon facebook-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 25"
                  fill="#d8d8d8"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M20.125 2.66699H3.875C2.84375 2.66699 2 3.51074 2 4.54199V20.792C2 21.8232 2.84375 22.667 3.875 22.667H12V13.917H9.5V11.417H12V10.167C12 8.10074 13.6838 6.41699 15.75 6.41699H18.25V8.91699H15.75C15.0625 8.91699 14.5 9.47949 14.5 10.167V11.417H18.25L17.625 13.917H14.5V22.667H20.125C21.1562 22.667 22 21.8232 22 20.792V4.54199C22 3.51074 21.1562 2.66699 20.125 2.66699Z" />
                </svg>
              </div>
            </a>
          </li>
          <li class="social-icon-item">
            <a href="#" class="visa-footer social-icon" aria-label="LinkedIn">
              <div class="icon linkedin-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 25"
                  fill="#d8d8d8"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M19.7778 2.66699C20.3671 2.66699 20.9324 2.90112 21.3491 3.31787C21.7659 3.73461 22 4.29984 22 4.88921V20.4448C22 21.0341 21.7659 21.5994 21.3491 22.0161C20.9324 22.4329 20.3671 22.667 19.7778 22.667H4.22222C3.63285 22.667 3.06762 22.4329 2.65087 22.0161C2.23413 21.5994 2 21.0341 2 20.4448V4.88921C2 4.29984 2.23413 3.73461 2.65087 3.31787C3.06762 2.90112 3.63285 2.66699 4.22222 2.66699H19.7778ZM19.2222 19.8892V14.0003C19.2222 13.0397 18.8406 12.1183 18.1613 11.439C17.482 10.7597 16.5607 10.3781 15.6 10.3781C14.6556 10.3781 13.5556 10.9559 13.0222 11.8225V10.5892H9.92222V19.8892H13.0222V14.4114C13.0222 13.5559 13.7111 12.8559 14.5667 12.8559C14.9792 12.8559 15.3749 13.0198 15.6666 13.3115C15.9583 13.6032 16.1222 13.9989 16.1222 14.4114V19.8892H19.2222ZM6.31111 8.84477C6.80618 8.84477 7.28098 8.6481 7.63104 8.29804C7.98111 7.94797 8.17778 7.47317 8.17778 6.9781C8.17778 5.94477 7.34444 5.10033 6.31111 5.10033C5.81309 5.10033 5.33547 5.29816 4.98331 5.65031C4.63116 6.00247 4.43333 6.48009 4.43333 6.9781C4.43333 8.01144 5.27778 8.84477 6.31111 8.84477ZM7.85556 19.8892V10.5892H4.77778V19.8892H7.85556Z" />
                </svg>
              </div>
            </a>
          </li>
          <li class="social-icon-item">
            <a href="#" class="visa-footer social-icon" aria-label="X">
              <div class="icon x-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 25"
                  fill="#d8d8d8"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M14.0566 11.2122L20.9777 3.16699H19.3376L13.3281 10.1525L8.52823 3.16699H2.99219L10.2505 13.7304L2.99219 22.167H4.63236L10.9786 14.79L16.0476 22.167H21.5836L14.0562 11.2122H14.0566ZM11.8102 13.8234L11.0748 12.7716L5.22333 4.40168H7.74254L12.4647 11.1564L13.2001 12.2083L19.3384 20.9884H16.8192L11.8102 13.8238V13.8234Z" />
                </svg>
              </div>
            </a>
          </li>
          <li class="social-icon-item">
            <a href="#" class="visa-footer social-icon" aria-label="Instagram">
              <div class="icon instagram-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 25"
                  fill="#d8d8d8"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12.002 2.66699C14.717 2.66699 15.058 2.67886 16.128 2.72949C17.19 2.77638 17.917 2.94809 18.554 3.19434C19.21 3.44813 19.769 3.79319 20.323 4.34766C20.881 4.90269 21.222 5.4606 21.468 6.125C21.714 6.75755 21.887 7.48824 21.934 8.55078C21.98 9.61781 21.992 9.9575 21.992 12.6729C21.992 15.3883 21.98 15.7285 21.934 16.7949C21.887 17.8575 21.714 18.5837 21.468 19.2207C21.214 19.877 20.87 20.4352 20.315 20.9902C19.761 21.5453 19.202 21.8898 18.546 22.1436C17.913 22.3898 17.183 22.5615 16.12 22.6084C15.054 22.6553 14.713 22.667 11.998 22.667C9.283 22.667 8.942 22.6553 7.876 22.6084C6.814 22.5615 6.087 22.3898 5.45 22.1436C4.794 21.8898 4.235 21.5447 3.681 20.9902C3.122 20.4358 2.782 19.8769 2.523 19.2168C2.277 18.5843 2.105 17.8535 2.059 16.791C2.012 15.724 2 15.3843 2 12.6689C2 9.95409 2.012 9.61438 2.059 8.54395C2.105 7.48128 2.277 6.75416 2.523 6.11719C2.781 5.46091 3.122 4.9027 3.681 4.34766C4.235 3.78901 4.794 3.4489 5.454 3.19141C6.087 2.9452 6.817 2.77344 7.88 2.72656C8.947 2.67969 9.287 2.66797 12.002 2.66797V2.66699ZM12.001 4.46875C9.329 4.46875 9.012 4.48046 7.961 4.52734C6.988 4.57046 6.457 4.73423 6.105 4.87109C5.64 5.05487 5.308 5.26919 4.956 5.62109C4.609 5.96927 4.39 6.30544 4.21 6.77051C4.073 7.12243 3.909 7.64923 3.866 8.62598C3.819 9.67739 3.808 9.99751 3.808 12.666C3.808 15.3346 3.819 15.6509 3.866 16.7061C3.909 17.6784 4.073 18.2096 4.21 18.5615C4.394 19.0259 4.608 19.3581 4.96 19.71C5.308 20.0581 5.643 20.277 6.108 20.457C6.46 20.5939 6.988 20.7577 7.965 20.8008C9.016 20.8477 9.336 20.8594 12.005 20.8594C14.673 20.8594 14.99 20.8477 16.045 20.8008C17.017 20.7576 17.548 20.5939 17.9 20.457C18.365 20.2733 18.697 20.058 19.049 19.7061C19.397 19.3587 19.615 19.0234 19.795 18.5586C19.932 18.2067 20.096 17.6792 20.139 16.7021C20.186 15.6508 20.198 15.3305 20.198 12.6621C20.198 9.99375 20.186 9.67721 20.139 8.62207C20.096 7.64901 19.932 7.1185 19.795 6.7666C19.611 6.30153 19.397 5.96912 19.045 5.61719C18.697 5.26978 18.361 5.05108 17.896 4.87109C17.545 4.73423 17.018 4.57047 16.041 4.52734C14.99 4.48046 14.674 4.46875 12.002 4.46875H12.001ZM12.002 7.53125C14.839 7.53138 17.14 9.83299 17.14 12.6689C17.14 15.5056 14.839 17.8065 12.002 17.8066C9.165 17.8066 6.864 15.5056 6.864 12.6689C6.864 9.83228 9.166 7.53125 12.002 7.53125ZM12.002 9.33691C10.162 9.33691 8.669 10.8287 8.669 12.6689C8.669 14.5092 10.162 16.001 12.002 16.001C13.842 16.0008 15.334 14.5092 15.334 12.6689C15.334 10.8288 13.842 9.33704 12.002 9.33691ZM17.342 6.12891C18.004 6.12891 18.541 6.66565 18.541 7.32812C18.541 7.99073 18.004 8.52832 17.342 8.52832C16.679 8.52832 16.143 7.99073 16.143 7.32812C16.143 6.66565 16.679 6.12891 17.342 6.12891Z" />
                </svg>
              </div>
            </a>
          </li>
        </ul>
      );
    }

    return (
      <ul class="visa-footer social-icons" role="list" aria-label="Social media links">
        {this.footerData.socialLinks.map((link, index) => {
          // Get the appropriate SVG icon based on the link name
          let iconSvg;
          switch (link.name.toLowerCase()) {
            case 'facebook':
              iconSvg = (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 25"
                  fill="#d8d8d8"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M20.125 2.66699H3.875C2.84375 2.66699 2 3.51074 2 4.54199V20.792C2 21.8232 2.84375 22.667 3.875 22.667H12V13.917H9.5V11.417H12V10.167C12 8.10074 13.6838 6.41699 15.75 6.41699H18.25V8.91699H15.75C15.0625 8.91699 14.5 9.47949 14.5 10.167V11.417H18.25L17.625 13.917H14.5V22.667H20.125C21.1562 22.667 22 21.8232 22 20.792V4.54199C22 3.51074 21.1562 2.66699 20.125 2.66699Z" />
                </svg>
              );
              break;
            case 'linkedin':
              iconSvg = (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 25"
                  fill="#d8d8d8"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M19.7778 2.66699C20.3671 2.66699 20.9324 2.90112 21.3491 3.31787C21.7659 3.73461 22 4.29984 22 4.88921V20.4448C22 21.0341 21.7659 21.5994 21.3491 22.0161C20.9324 22.4329 20.3671 22.667 19.7778 22.667H4.22222C3.63285 22.667 3.06762 22.4329 2.65087 22.0161C2.23413 21.5994 2 21.0341 2 20.4448V4.88921C2 4.29984 2.23413 3.73461 2.65087 3.31787C3.06762 2.90112 3.63285 2.66699 4.22222 2.66699H19.7778ZM19.2222 19.8892V14.0003C19.2222 13.0397 18.8406 12.1183 18.1613 11.439C17.482 10.7597 16.5607 10.3781 15.6 10.3781C14.6556 10.3781 13.5556 10.9559 13.0222 11.8225V10.5892H9.92222V19.8892H13.0222V14.4114C13.0222 13.5559 13.7111 12.8559 14.5667 12.8559C14.9792 12.8559 15.3749 13.0198 15.6666 13.3115C15.9583 13.6032 16.1222 13.9989 16.1222 14.4114V19.8892H19.2222ZM6.31111 8.84477C6.80618 8.84477 7.28098 8.6481 7.63104 8.29804C7.98111 7.94797 8.17778 7.47317 8.17778 6.9781C8.17778 5.94477 7.34444 5.10033 6.31111 5.10033C5.81309 5.10033 5.33547 5.29816 4.98331 5.65031C4.63116 6.00247 4.43333 6.48009 4.43333 6.9781C4.43333 8.01144 5.27778 8.84477 6.31111 8.84477ZM7.85556 19.8892V10.5892H4.77778V19.8892H7.85556Z" />
                </svg>
              );
              break;
            case 'x':
            case 'twitter':
              iconSvg = (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 25"
                  fill="#d8d8d8"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M14.0566 11.2122L20.9777 3.16699H19.3376L13.3281 10.1525L8.52823 3.16699H2.99219L10.2505 13.7304L2.99219 22.167H4.63236L10.9786 14.79L16.0476 22.167H21.5836L14.0562 11.2122H14.0566ZM11.8102 13.8234L11.0748 12.7716L5.22333 4.40168H7.74254L12.4647 11.1564L13.2001 12.2083L19.3384 20.9884H16.8192L11.8102 13.8238V13.8234Z" />
                </svg>
              );
              break;
            case 'instagram':
              iconSvg = (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 25"
                  fill="#d8d8d8"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12.002 2.66699C14.717 2.66699 15.058 2.67886 16.128 2.72949C17.19 2.77638 17.917 2.94809 18.554 3.19434C19.21 3.44813 19.769 3.79319 20.323 4.34766C20.881 4.90269 21.222 5.4606 21.468 6.125C21.714 6.75755 21.887 7.48824 21.934 8.55078C21.98 9.61781 21.992 9.9575 21.992 12.6729C21.992 15.3883 21.98 15.7285 21.934 16.7949C21.887 17.8575 21.714 18.5837 21.468 19.2207C21.214 19.877 20.87 20.4352 20.315 20.9902C19.761 21.5453 19.202 21.8898 18.546 22.1436C17.913 22.3898 17.183 22.5615 16.12 22.6084C15.054 22.6553 14.713 22.667 11.998 22.667C9.283 22.667 8.942 22.6553 7.876 22.6084C6.814 22.5615 6.087 22.3898 5.45 22.1436C4.794 21.8898 4.235 21.5447 3.681 20.9902C3.122 20.4358 2.782 19.8769 2.523 19.2168C2.277 18.5843 2.105 17.8535 2.059 16.791C2.012 15.724 2 15.3843 2 12.6689C2 9.95409 2.012 9.61438 2.059 8.54395C2.105 7.48128 2.277 6.75416 2.523 6.11719C2.781 5.46091 3.122 4.9027 3.681 4.34766C4.235 3.78901 4.794 3.4489 5.454 3.19141C6.087 2.9452 6.817 2.77344 7.88 2.72656C8.947 2.67969 9.287 2.66797 12.002 2.66797V2.66699ZM12.001 4.46875C9.329 4.46875 9.012 4.48046 7.961 4.52734C6.988 4.57046 6.457 4.73423 6.105 4.87109C5.64 5.05487 5.308 5.26919 4.956 5.62109C4.609 5.96927 4.39 6.30544 4.21 6.77051C4.073 7.12243 3.909 7.64923 3.866 8.62598C3.819 9.67739 3.808 9.99751 3.808 12.666C3.808 15.3346 3.819 15.6509 3.866 16.7061C3.909 17.6784 4.073 18.2096 4.21 18.5615C4.394 19.0259 4.608 19.3581 4.96 19.71C5.308 20.0581 5.643 20.277 6.108 20.457C6.46 20.5939 6.988 20.7577 7.965 20.8008C9.016 20.8477 9.336 20.8594 12.005 20.8594C14.673 20.8594 14.99 20.8477 16.045 20.8008C17.017 20.7576 17.548 20.5939 17.9 20.457C18.365 20.2733 18.697 20.058 19.049 19.7061C19.397 19.3587 19.615 19.0234 19.795 18.5586C19.932 18.2067 20.096 17.6792 20.139 16.7021C20.186 15.6508 20.198 15.3305 20.198 12.6621C20.198 9.99375 20.186 9.67721 20.139 8.62207C20.096 7.64901 19.932 7.1185 19.795 6.7666C19.611 6.30153 19.397 5.96912 19.045 5.61719C18.697 5.26978 18.361 5.05108 17.896 4.87109C17.545 4.73423 17.018 4.57047 16.041 4.52734C14.99 4.48046 14.674 4.46875 12.002 4.46875H12.001ZM12.002 7.53125C14.839 7.53138 17.14 9.83299 17.14 12.6689C17.14 15.5056 14.839 17.8065 12.002 17.8066C9.165 17.8066 6.864 15.5056 6.864 12.6689C6.864 9.83228 9.166 7.53125 12.002 7.53125ZM12.002 9.33691C10.162 9.33691 8.669 10.8287 8.669 12.6689C8.669 14.5092 10.162 16.001 12.002 16.001C13.842 16.0008 15.334 14.5092 15.334 12.6689C15.334 10.8288 13.842 9.33704 12.002 9.33691ZM17.342 6.12891C18.004 6.12891 18.541 6.66565 18.541 7.32812C18.541 7.99073 18.004 8.52832 17.342 8.52832C16.679 8.52832 16.143 7.99073 16.143 7.32812C16.143 6.66565 16.679 6.12891 17.342 6.12891Z" />
                </svg>
              );
              break;
            default:
              iconSvg = <span class={link.icon}></span>;
          }

          // If it has a handler (like Twitter with multiple accounts)
          if (link.handler && link.handler.length > 0) {
            return (
              <li class="social-icon-item" key={`social-${index}`}>
                <div class="visa-footer vs-footer-social-dropdown">
                  <button
                    class="visa-footer vs-footer-social-button vs-footer-social-dropdown-toggle"
                    onClick={e => this.toggleSocialDropdown(link.name, e)}
                    aria-label={`${link.name} social media links`}
                  >
                    <span class="visa-footer vs-footer-social-icon">{iconSvg}</span>
                  </button>
                  {this.socialDropdownOpen[link.name] && (
                    <div class="visa-footer vs-footer-social-dropdown-menu">
                      <ul class="visa-footer vs-footer-social-dropdown-list">
                        {link.handler.map((handler, handlerIndex) => (
                          <li
                            class="visa-footer vs-footer-social-dropdown-item"
                            key={`handler-${handlerIndex}`}
                          >
                            <a
                              href={handler.value}
                              target={handler.target === '_new' ? '_blank' : '_self'}
                              rel={handler.linkType === 'external' ? 'noopener noreferrer' : null}
                              class="visa-footer vs-footer-social-dropdown-link"
                              aria-label={`${link.name} - ${handler.key}`}
                            >
                              {handler.key}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </li>
            );
          }

          // Otherwise, it's a simple link
          return (
            <li class="social-icon-item" key={`social-${index}`}>
              <a
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                class="visa-footer social-icon"
                aria-label={link.name}
              >
                <div class="icon">{iconSvg}</div>
              </a>
            </li>
          );
        })}
      </ul>
    );
  }

  /**
   * Render the copyright text
   */
  private renderCopyright() {
    if (this.footerData?.copyright) {
      // Use the copyright text from footerData
      return (
        <p class="visa-footer copyright">
          {this.footerData.copyright.replace('{year}', this.currentYear)}
        </p>
      );
    }

    // Fallback to default copyright text
    return (
      <p class="visa-footer copyright">
        ©Copyright 1996 - {this.currentYear}. All rights reserved.
      </p>
    );
  }

  /**
   * Render the Visa logo
   */
  private renderVisaLogo() {
    return (
      <div class="visa-footer visa-logo-container">
        <svg
          width="1335"
          height="430"
          viewBox="0 0 1336 430"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M507.369 7.60031L332.588 423.495H218.557L132.547 91.592C127.325 71.1489 122.785 63.6595 106.904 55.0468C80.9894 41.0181 38.172 27.8632 0.5 19.6942L3.05875 7.60031H186.614C210.012 7.60031 231.045 23.1338 236.357 50.0053L281.782 290.663L394.047 7.60031H507.369ZM954.17 287.709C954.629 177.942 801.98 171.895 803.03 122.86C803.356 107.937 817.603 92.0705 848.788 88.0207C864.245 86.0028 906.833 84.4633 955.136 106.633L974.083 18.4391C948.127 9.0427 914.732 0 873.18 0C766.554 0 691.515 56.5308 690.883 137.478C690.194 197.351 744.443 230.762 785.313 250.658C827.359 271.031 841.466 284.124 841.307 302.348C841.008 330.246 807.772 342.562 776.712 343.047C722.492 343.879 691.029 328.415 665.949 316.786L646.397 407.899C671.602 419.432 718.125 429.494 766.359 430C879.688 430 953.822 374.17 954.17 287.709ZM1235.73 423.502H1335.5L1248.41 7.60031H1156.32C1135.62 7.60031 1118.15 19.6249 1110.42 38.1125L948.545 423.495H1061.82L1084.31 361.368H1222.71L1235.74 423.495L1235.73 423.502ZM1115.36 276.135L1172.14 119.982L1204.82 276.135H1115.37H1115.36ZM661.506 7.60031L572.304 423.495H464.433L553.67 7.60031H661.506Z"
            fill="white"
          />
        </svg>
      </div>
    );
  }

  private renderBottomPanel() {
    const countryMap = {
      AL: 'Albania',
      DZ: 'Algeria',
      AD: 'Andorra',
      AO: 'Angola',
      US: 'United States',
      CA: 'Canada',
      MX: 'Mexico',
      UK: 'United Kingdom',
    };
    return (
      <div
        class={`visa-footer visa-footer-bottom-panel ${this.showCountryPanel ? 'show' : ''}`}
        role="dialog"
        aria-labelledby="benefit-panel-title"
        aria-hidden={this.showCountryPanel ? 'false' : 'true'}
        aria-modal={this.showCountryPanel ? 'true' : 'false'}
        id="benefit-panel"
      >
        <button
          class="visa-footer visa-footer-close-btn"
          aria-label="Close country/region selection panel"
          aria-controls="benefit-panel"
          onClick={() => this.closeCountryPanel()}
        >
          <span class="visa-footer visa-footer-close-icon" aria-hidden="true"></span>
        </button>

        <div class="visa-footer visa-footer-panel-content">
          <div class="visa-footer visa-footer-panel-left">
            <h2 class="visa-footer visa-footer-panel-name" id="benefit-panel-title">
              Select Country/Region
            </h2>
          </div>
          <div class="visa-footer visa-footer-panel-divider" aria-hidden="true"></div>
          <div
            class="visa-footer visa-footer-panel-right"
            ref={el => (this.panelRightEl = el as HTMLElement)}
          >
            <div class="visa-footer visa-footer-selected-language">
              <div class="visa-footer-selected-language-info label">Your Locale</div>
              <div class="visa-footer-selected-language-info language">
                {this.selectedCountry} - {this.selectedLanguage}
              </div>
            </div>
            <ul
              class="visa-footer visa-footer-card-list"
              role="listbox"
              aria-label="Available countries"
            >
              {this.footerData?.countries?.options?.map((option, index) => {
                let countryName = option.key;

                if (countryMap[option.key]) {
                  countryName = countryMap[option.key];
                } else if (!option.value.includes('://')) {
                  countryName = option.value;
                }

                return (
                  <li
                    key={`country-${index}`}
                    class={{
                      'analytics-footer-button visa-footer': true,
                      'visa-footer visa-footer-card-item': true,
                      active: option.key === this.selectedCountry,
                    }}
                    role="option"
                    aria-selected={option.key === this.selectedCountry ? 'true' : 'false'}
                    id={`country-option-${option.key.toLowerCase()}`}
                    data-language-url={option.value}
                    tabindex="0"
                    onClick={e => this.handleCountrySelection(option.key, option.value, e)}
                    onKeyDown={e => this.handleCountryKeyDown(option.key, option.value, e)}
                    aria-label={`Select ${countryName} as your country`}
                  >
                    {countryName}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render the new footer design
   */
  private renderNewFooterDesign() {
    return (
      <div class="vsr-footer-container visa-footer">
        <section
          class="navigation-container visa-footer"
          aria-labelledby="footer-navigation-heading"
        >
          <h2 class="vs-visually-hidden" id="footer-navigation-heading">
            Footer Navigation
          </h2>
          <h2 class="vs-visually-hidden" id="news-media-heading">
            News and media
          </h2>
          {this.renderNavigationSections()}
          <aside
            class="language-selector-container visa-footer"
            role="complementary"
            aria-label="Language and region selection"
          >
            {this.renderLanguageSelector()}
          </aside>
        </section>
        <section class="footer-bottom visa-footer" aria-labelledby="footer-info-heading">
          <h2 class="vs-visually-hidden" id="footer-info-heading">
            Footer Information
          </h2>
          <aside
            class="social-icons-container visa-footer"
            role="complementary"
            aria-label="Social media links"
          >
            {this.renderSocialIcons()}
          </aside>
          <div
            class="copyright-container visa-footer"
            role="contentinfo"
            aria-label="Copyright information"
          >
            {this.renderCopyright()}
          </div>
        </section>

        <aside
          class="visa-logo-container visa-footer"
          role="complementary"
          aria-label="Visa company logo"
        >
          {this.renderVisaLogo()}
        </aside>
        {this.renderBottomPanel()}
      </div>
    );
  }

  render() {
    if (!this.footerData) {
      return <div class="vs-footer-loading">Loading...</div>;
    }

    return (
      <footer class="vs-footer" role="contentinfo" aria-label="Site footer">
        {this.renderNewFooterDesign()}
      </footer>
    );
  }
}
